import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import {
  CheckIcon,
  ReviewDocIcon,
  UploadIcon,
} from "../../../../components/organization/gateIcons";
import {
  ACCEPTED_DOCUMENT_TYPES,
  MAX_DOCUMENT_SIZE_BYTES,
} from "../../../../components/organization/organizationDocuments.constants";
import { uploadFileWithPresignedUrl } from "../../../../s3Integration/s3Client";
import { EUploadImageType } from "../../../../s3Integration/enums";
import { IPartyBankDocument } from "../../../../setup/util/partyCountry.helpers";
import styles from "./styles.module.scss";

interface Props {
  /** Signed-in user's organization; uploads outside images/organizations/<code>/ get a 403. */
  organizationCode: string | null;
  /** The party's row id — the object is keyed under it. */
  partyId: string;
  value?: IPartyBankDocument | null;
  onChange: (document: IPartyBankDocument | null) => void;
  onUploadingChange?: (uploading: boolean) => void;
  errorMessage?: string;
}

/**
 * Optional bank-info document an international party attaches (IBAN letter, statement, …).
 *
 * Uploaded privately the moment it is picked, like the organization onboarding documents, so the
 * party only carries the bucket + key; the admin opens it through a presigned GET. The key is
 * images/organizations/<orgCode>/contract-parties/<partyRowId>/<timestamp>-<random>.<ext> — the
 * only prefix the presign service grants business-portal users.
 */
const BankDocumentUpload: React.FC<Props> = ({
  organizationCode,
  partyId,
  value,
  onChange,
  onUploadingChange,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const setUploadingState = (next: boolean) => {
    setUploading(next);
    onUploadingChange?.(next);
  };

  const onPick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so re-picking the same file after an error still fires onChange.
    e.target.value = "";
    if (!file) return;

    if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
      setLocalError(t("lbl_org_docs_invalid_type"));
      return;
    }
    if (file.size > MAX_DOCUMENT_SIZE_BYTES) {
      setLocalError(t("lbl_org_docs_too_large"));
      return;
    }

    if (!organizationCode) {
      setLocalError(t("err_party_bank_document_upload"));
      return;
    }

    setLocalError(null);
    setUploadingState(true);
    try {
      const { bucket, key } = await uploadFileWithPresignedUrl(
        file,
        EUploadImageType.ORGANIZATION,
        `${organizationCode}/contract-parties/${partyId}`
      );
      onChange({ bucketName: bucket, objectName: key, fileName: file.name });
    } catch {
      setLocalError(t("err_party_bank_document_upload"));
    } finally {
      setUploadingState(false);
    }
  };

  const shownError = localError || errorMessage;
  const fileName = value?.fileName || value?.objectName?.split("/").pop();

  return (
    <label
      className={classNames(styles.docUpload, {
        [styles.hasFile]: !!value && !shownError,
        [styles.hasError]: !!shownError,
        [styles.isUploading]: uploading,
      })}>
      <input
        type="file"
        accept={ACCEPTED_DOCUMENT_TYPES.join(",")}
        hidden
        disabled={uploading}
        onChange={onPick}
      />
      <span className={styles.duIcon}>
        <ReviewDocIcon />
      </span>
      <span className={styles.duTxt}>
        <b>{t("lbl_party_bank_document")}</b>
        {shownError ? (
          <span className={styles.duError}>{shownError}</span>
        ) : uploading ? (
          <span className={styles.duHint}>{t("lbl_uploading")}</span>
        ) : value ? (
          // dir="auto" so a Latin file name ellipsizes at its own end.
          <span className={styles.duFile} dir="auto">
            {fileName}
          </span>
        ) : (
          <span className={styles.duHint}>{t("lbl_org_docs_hint")}</span>
        )}
      </span>
      <span className={styles.duCta}>
        {uploading ? (
          <span className={styles.duSpinner} />
        ) : (
          <>
            <UploadIcon />
            {value ? t("lbl_org_docs_change") : t("lbl_org_docs_upload")}
          </>
        )}
      </span>
      {value && !shownError && !uploading && (
        <span className={styles.duDone}>
          <CheckIcon />
        </span>
      )}
    </label>
  );
};

export default BankDocumentUpload;
